import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { addHours, format, isValid, parse, startOfDay } from 'date-fns';
import { WorkOrderServiceTypeLabels, WorkOrderSource } from '../../../enums/work-order.enum';
import {
  CreateWorkOrderRequest,
  WorkOrderServiceTypeOption,
  WorkshopOption
} from '../../../models/work-order.models';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrderService } from '../../../services/work-order.service';
import { ServiceTypes } from '../../../../../_shared/enums/service-type.enum';
import { AssetDropdownItem, AssetSearchDropdownComponent } from '../../../../../_shared/components/asset-search-dropdown/asset-search-dropdown.component';
import { MaintenanceService } from '../../../../../_shared/services/maintenance.service';
import { MaintenanceReason } from '../../../../../_shared/models/maintenance.model';
import { MaintenanceStatus } from '../../../../../_shared/enums/maintenance-status.enum';
import { MaintenanceModel } from '../../../../../_shared/enums/maintenance-model.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-create-work-order-modal',
  standalone: true,
  imports: [NgbModule, CommonModule, FormsModule, FlatpickrDatePickerComponent, AssetSearchDropdownComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './create-work-order-modal.component.html',
  styleUrl: './create-work-order-modal.component.scss'
})
export class CreateWorkOrderModalComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private lookupService = inject(LookupService);
  private maintenanceService = inject(MaintenanceService);
  private errorHandlerService = inject(ErrorHandlerService);
  private workOrderService = inject(WorkOrderService);
  private toastr = inject(ToastrService);
  private modalInstance = inject(NgbActiveModal);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  currencySymbol: string = '';
  formData: CreateWorkOrderRequest = {
    assetId: 0,
    workshopId: 0,
    maintenanceSource: WorkOrderSource.System,
    maintenanceModel: MaintenanceModel.InHouse,
    maintenanceStatus: MaintenanceStatus.Approved,
    maintenanceReasonId: 0,
    serviceType: 0 as ServiceTypes,
    scheduledDate: new Date(''),
    dueDate: new Date(''),
    taskDescription: '',
    notes: '',
    estimatedCost: undefined,
    estimatedTime: undefined,
    enableEmailAlert: false,
    isAutoTrigger: false
  };

  isLoading: boolean = false;
  isLoadingWorkshops: boolean = false;
  isLoadingReasons: boolean = false;
  timeSuffixLeft: number = 24;
  selectedAsset: AssetDropdownItem | null = null;
  workshops: WorkshopOption[] = [];
  maintenanceReasons: MaintenanceReason[] = [];

  touched = {
    asset: false,
    serviceType: false,
    scheduledDate: false,
    workshop: false,
    maintenanceReason: false
  };

  serviceTypeDropdownOpen: boolean = false;
  workshopDropdownOpen: boolean = false;
  maintenanceReasonDropdownOpen: boolean = false;

  readonly serviceTypes: WorkOrderServiceTypeOption[] = [
    { id: ServiceTypes.Minor, name: WorkOrderServiceTypeLabels[ServiceTypes.Minor] },
    { id: ServiceTypes.Intermediate, name: WorkOrderServiceTypeLabels[ServiceTypes.Intermediate] },
    { id: ServiceTypes.Major, name: WorkOrderServiceTypeLabels[ServiceTypes.Major] }
  ];

  ngOnInit(): void {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);
    void this.loadMaintenanceReasons();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get selectedServiceTypeName(): string {
    if (!this.formData.serviceType) return '';
    const key = WorkOrderServiceTypeLabels[this.formData.serviceType];
    return key ? this.transloco.translate(key) : '';
  }

  get selectedWorkshopName(): string {
    return this.workshops.find(w => w.id === this.formData.workshopId)?.name || '';
  }

  get selectedMaintenanceReasonName(): string {
    const reason = this.maintenanceReasons.find(reason => reason.id === this.formData.maintenanceReasonId);
    return reason?.localizedName || reason?.name || '';
  }

  get isFormValid(): boolean {
    return (
      this.formData.assetId > 0 &&
      this.formData.serviceType > 0 &&
      this.hasValidScheduledDate &&
      this.formData.workshopId > 0 &&
      this.formData.maintenanceReasonId > 0
    );
  }

  get scheduledDateValue(): string | undefined {
    if (!this.hasValidScheduledDate) {
      return '';
    }

    return this.formatDateForPicker(this.formData.scheduledDate);
  }

  toggleServiceTypeDropdown(): void {
    if (this.serviceTypeDropdownOpen) {
      this.touched.serviceType = true;
    }

    this.serviceTypeDropdownOpen = !this.serviceTypeDropdownOpen;
    this.workshopDropdownOpen = false;
    this.maintenanceReasonDropdownOpen = false;
  }

  get isWorkshopDisabled(): boolean {
    return this.formData.assetId <= 0 || this.isLoadingWorkshops;
  }

  toggleWorkshopDropdown(): void {
    if (this.isWorkshopDisabled) {
      return;
    }

    if (this.workshopDropdownOpen) {
      this.touched.workshop = true;
    }

    this.workshopDropdownOpen = !this.workshopDropdownOpen;
    this.serviceTypeDropdownOpen = false;
    this.maintenanceReasonDropdownOpen = false;
  }

  toggleMaintenanceReasonDropdown(): void {
    if (this.maintenanceReasonDropdownOpen) {
      this.touched.maintenanceReason = true;
    }

    this.maintenanceReasonDropdownOpen = !this.maintenanceReasonDropdownOpen;
    this.serviceTypeDropdownOpen = false;
    this.workshopDropdownOpen = false;
  }

  selectServiceType(serviceType: WorkOrderServiceTypeOption): void {
    this.formData.serviceType = serviceType.id;
    this.serviceTypeDropdownOpen = false;
  }

  selectWorkshop(workshop: WorkshopOption): void {
    this.formData.workshopId = workshop.id;
    this.workshopDropdownOpen = false;
  }

  selectMaintenanceReason(reason: MaintenanceReason): void {
    this.formData.maintenanceReasonId = reason.id;
    this.maintenanceReasonDropdownOpen = false;
  }

  onScheduledDateChange(value: string | undefined): void {
    this.formData.scheduledDate = value ? this.parsePickerDate(value) : new Date('');
    this.touched.scheduledDate = true;
  }

  closeAllDropdowns(): void {
    this.serviceTypeDropdownOpen = false;
    this.workshopDropdownOpen = false;
    this.maintenanceReasonDropdownOpen = false;
  }

  onAssetSelect(asset: AssetDropdownItem): void {
    if (this.formData.assetId !== asset.assetId) {
      this.resetWorkshopSelection();
    }

    this.selectedAsset = asset;
    this.formData.assetId = asset.assetId;
    this.touched.asset = true;
    this.touched.workshop = false;
    this.workshopDropdownOpen = false;
    this.formData.maintenanceModel = asset.maintenanceModel ?? MaintenanceModel.InHouse;

    void this.loadWorkshopsForSelectedAsset();
  }

  onEstimatedTimeInputChange(): void {
    const value = this.formData.estimatedTime;
    if (value === undefined || value === null) {
      this.timeSuffixLeft = 18;
      return;
    }

    const valueStr = value.toString();
    const charWidth = 7;
    this.timeSuffixLeft = 14 + (valueStr.length * charWidth) + 6;
  }

  async createWorkOrder(): Promise<void> {
    if (!this.isFormValid) {
      this.markFormTouched();
      return;
    }

    this.isLoading = true;

    try {
      const payload: CreateWorkOrderRequest = {
        ...this.formData,
        maintenanceModel: this.selectedAsset?.maintenanceModel ?? MaintenanceModel.InHouse,
        maintenanceStatus: MaintenanceStatus.Approved,
        dueDate: this.calculateDueDate(this.formData.scheduledDate, this.formData.estimatedTime),
        taskDescription: undefined,
        estimatedCost: this.normalizeOptionalNumber(this.formData.estimatedCost),
        estimatedTime: this.normalizeOptionalNumber(this.formData.estimatedTime),
        isAutoTrigger: false
      };

      await this.workOrderService.createWorkOrderAsync(payload, this.cancelSubject);

      this.toastr.success(this.transloco.translate('workOrder.toasts.created'));
      this.modalInstance.close(true);
    } catch (error: unknown) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private async loadMaintenanceReasons(): Promise<void> {
    this.isLoadingReasons = true;

    try {
      const response = await this.maintenanceService.getMaintenanceReasons(this.cancelSubject);
      this.maintenanceReasons = response.data ?? [];

      this.isLoadingReasons = false
    } catch (error: unknown) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.maintenanceReasons = [];
          this.isLoadingReasons = false;
        }
      });
    }
  }

  private async loadWorkshopsForSelectedAsset(): Promise<void> {
    if (!this.selectedAsset) {
      this.resetWorkshopSelection();
      return;
    }

    this.isLoadingWorkshops = true;

    try {
      const response = await this.lookupService.getWorkshopsAsync([this.selectedAsset.accountId]);
      this.workshops = response.data ?? [];
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
      this.workshops = [];
    } finally {
      this.isLoadingWorkshops = false;
    }
  }

  private markFormTouched(): void {
    this.touched.asset = true;
    this.touched.serviceType = true;
    this.touched.scheduledDate = true;
    this.touched.workshop = true;
    this.touched.maintenanceReason = true;
  }

  private resetWorkshopSelection(): void {
    this.formData.workshopId = 0;
    this.workshops = [];
  }

  get hasValidScheduledDate(): boolean {
    return !Number.isNaN(this.formData.scheduledDate.getTime());
  }

  private normalizeOptionalNumber(value: number | string | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const normalizedValue = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(normalizedValue) ? undefined : normalizedValue;
  }

  private calculateDueDate(scheduledDate: Date, estimatedTime: number | string | undefined): Date {
    const normalizedEstimatedTime = this.normalizeOptionalNumber(estimatedTime) ?? 0;
    return startOfDay(addHours(scheduledDate, normalizedEstimatedTime));
  }

  private formatDateForPicker(value: Date): string {
    return format(value, 'yyyy-MM-dd HH:mm');
  }

  private parsePickerDate(value: string): Date {
    const parsedDateTime = parse(value.trim(), 'yyyy-MM-dd HH:mm', new Date());
    if (isValid(parsedDateTime)) {
      return parsedDateTime;
    }

    const parsedDateOnly = parse(value.trim(), 'yyyy-MM-dd', new Date());
    return isValid(parsedDateOnly) ? parsedDateOnly : new Date('');
  }
}